import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { recordBookedMeeting } from '@/api/endpoints/meetings';
import SwoftSlotPicker from './SwoftSlotPicker';
import { formatPickerDay, formatSlotTime, slotToChoice, type AvailabilitySlot } from '@/services/dialer/slotPicker';
import { currentLocale } from '@/i18n/locale';
import { DAY_LABELS, MONTH_NAMES, buildCalendarGrid, isDatePast } from '@/services/dialer/calendarGrid';
import {
  bookingLinkUrl,
  buildBookedMeetingRequest,
  feedbackKeyFor,
  offersSwoftSlots,
  showsBookingLink,
  type CustomerCrmView,
} from '@/services/dialer/bookedMeeting';

/**
 * The date, the time and the "enregistrer" button of a meeting already
 * obtained. Shared, deliberately, by the two screens Kevin named:
 *
 *   - the prospect card, where the prospect is the one on screen;
 *   - the left-nav entry, where the prospect is picked from a list.
 *
 * The prospect arrives as a prop, and everything after it is identical:
 * one request built by buildBookedMeetingRequest, one call to
 * recordBookedMeeting, one message back to the caller. Nothing here decides
 * which prospect it is, which is the only thing the two paths do differently.
 *
 * Beside the date sits the client's booking link, but only when that client
 * has no Swoft CRM connected and a link is actually configured. No link
 * means no button: a button that leads nowhere is never shown.
 *
 * When the client runs the Swoft CRM, the date and time come from a free
 * slot of the person who holds its meetings (SwoftSlotPicker), read from
 * Swoft. Picking a slot fills the same date and time the manual entry fills,
 * so the request is unchanged. The manual entry stays one click away, for
 * a time agreed outside the host's calendar or when Swoft cannot answer.
 */

const HOURS = Array.from({ length: 13 }, (_, i) => String(i + 8).padStart(2, '0')); // 08 -> 20
const MINUTES = ['00', '15', '30', '45'];
const DURATION_VALUES = ['15', '30', '45', '60'];
const DURATION_KEY: Record<string, string> = { 15: 'min15', 30: 'min30', 45: 'min45', 60: 'hour1' };

export interface BookedMeetingFormProps {
  /** The prospect the meeting was booked with; null disables the button. */
  prospectId: string | null;
  /** The client in scope; decides the booking-link button. */
  customer: CustomerCrmView | null;
  onDone?: () => void;
  onCancel?: () => void;
  /** Hides the cancel button where the screen already has its own way out. */
  compact?: boolean;
  /** The call page passes the call the meeting was obtained on. */
  parallelCallId?: string | null;
}

export default function BookedMeetingForm({ prospectId, customer, onDone, onCancel, compact = false, parallelCallId = null }: BookedMeetingFormProps) {
  const { t } = useTranslation(['dialer', 'common']);

  const today = new Date();
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [calYear, setCalYear] = useState(today.getFullYear());
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [hour, setHour] = useState('09');
  const [minute, setMinute] = useState('00');
  const [duration, setDuration] = useState('30');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
  // Le calendrier Swoft a refuse de repondre pour ce client : on n'y revient
  // pas, et le lien de prise de rendez-vous prend le relais.
  const [creneauxIndisponibles, setCreneauxIndisponibles] = useState(false);

  const calGrid = useMemo(() => buildCalendarGrid(calYear, calMonth), [calYear, calMonth]);
  const linkUrl = bookingLinkUrl(customer);
  const withSwoftSlots = offersSwoftSlots(customer) && !creneauxIndisponibles;
  // Le lien ne s'affiche ici QUE comme porte de secours : le client a un CRM,
  // donc ce formulaire est le bon geste, mais le calendrier Swoft vient de
  // refuser de repondre. Sans CRM, on n'arrive jamais dans ce formulaire :
  // le lien EST le geste (BookedOnLinkPanel).
  const withBookingLink = creneauxIndisponibles && showsBookingLink(customer, false);
  const slotMode = withSwoftSlots && !manualEntry;
  const hasDate = slotMode ? selectedSlot !== null : selectedDay !== null;
  const canSubmit = prospectId !== null && hasDate && !submitting;

  const pickSlot = (slot: AvailabilitySlot) => {
    const choice = slotToChoice(slot);
    setSelectedSlot(slot);
    setCalYear(choice.year);
    setCalMonth(choice.month);
    setSelectedDay(choice.day);
    setHour(choice.hour);
    setMinute(choice.minute);
  };

  const changeDuration = (value: string) => {
    setDuration(value);
    // A slot is free for the length it was asked for; another length asks
    // Swoft again, and the previous pick may no longer fit.
    if (slotMode) setSelectedSlot(null);
  };

  const enterManually = () => {
    setManualEntry(true);
    setSelectedSlot(null);
    // A slot can start at a time the manual pickers do not list.
    if (!HOURS.includes(hour) || !MINUTES.includes(minute)) {
      setHour('09');
      setMinute('00');
    }
  };

  /** Swoft ne peut pas donner de creneaux : saisie manuelle, et le lien. */
  const creneauxImpossibles = useCallback(() => {
    setCreneauxIndisponibles(true);
    setManualEntry(true);
    setSelectedSlot(null);
  }, []);

  const backToSlots = () => {
    setManualEntry(false);
    setSelectedSlot(null);
    setSelectedDay(null);
  };

  const prevMonth = () => {
    if (calMonth === 0) { setCalMonth(11); setCalYear((y) => y - 1); } else setCalMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalMonth(0); setCalYear((y) => y + 1); } else setCalMonth((m) => m + 1);
  };

  const handleConfirm = async () => {
    if (!prospectId || selectedDay === null || (slotMode && !selectedSlot)) return;
    setSubmitting(true);
    try {
      const result = await recordBookedMeeting(buildBookedMeetingRequest({
        prospectId,
        year: calYear,
        month: calMonth,
        day: selectedDay,
        hour,
        minute,
        durationMinutes: Number(duration),
        notes,
        parallelCallId,
      }));
      const client = result.customerName || t('dialer:bookedMeeting.thisClient');
      toast.success(t(`dialer:bookedMeeting.toast.${feedbackKeyFor(result.crmOutcome)}`, { client }));
      onDone?.();
    } catch (err) {
      const message = (err as { message?: string })?.message || t('dialer:bookedMeeting.toast.unknownError');
      toast.error(t('dialer:bookedMeeting.toast.error', { message }));
    } finally {
      setSubmitting(false);
    }
  };

  const durationField = (
    <label className="space-y-1.5 block">
      <span className="text-sm font-medium text-foreground/80">{t('dialer:bookedMeeting.durationLabel')}</span>
      <select
        value={duration}
        onChange={(e) => changeDuration(e.target.value)}
        className="w-full h-9 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
      >
        {DURATION_VALUES.map((value) => (
          <option key={value} value={value}>{t(`dialer:booking.duration.${DURATION_KEY[value]}`)}</option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-4">
      {slotMode && customer?.id ? (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3 items-end">
            <p className="col-span-2 text-sm font-medium text-foreground/80 pb-2">{t('dialer:bookedMeeting.slots.title')}</p>
            {durationField}
          </div>
          <SwoftSlotPicker
            customerId={customer.id}
            durationMinutes={Number(duration)}
            selectedStart={selectedSlot?.start ?? null}
            onSelect={pickSlot}
            onUnavailable={creneauxImpossibles}
          />
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-foreground/80 min-w-0 truncate">
              {selectedSlot && t('dialer:bookedMeeting.slots.selected', {
                day: formatPickerDay(new Date(selectedSlot.start), currentLocale()),
                time: formatSlotTime(selectedSlot.start),
              })}
            </p>
            <Button type="button" variant="link" size="sm" className="px-0 shrink-0" onClick={enterManually}>
              {t('dialer:bookedMeeting.slots.manualEntry')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-foreground/80">{t('dialer:bookedMeeting.dateLabel')}</p>
              {withSwoftSlots && (
                <Button type="button" variant="link" size="sm" className="px-0" onClick={backToSlots}>
                  {t('dialer:bookedMeeting.slots.backToSlots')}
                </Button>
              )}
              {withBookingLink && linkUrl && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(linkUrl, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  {t('dialer:bookedMeeting.bookingLink')}
                </Button>
              )}
            </div>
            <div className="border border-border rounded-xl overflow-hidden bg-card">
              <div className="flex items-center justify-between px-4 py-3 surface-1 border-b border-border/50">
                <button
                  type="button"
                  onClick={prevMonth}
                  aria-label={t('dialer:bookedMeeting.previousMonth')}
                  className="p-1.5 hover:bg-accent rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                </button>
                <span className="text-sm font-semibold text-foreground">{MONTH_NAMES[calMonth]} {calYear}</span>
                <button
                  type="button"
                  onClick={nextMonth}
                  aria-label={t('dialer:bookedMeeting.nextMonth')}
                  className="p-1.5 hover:bg-accent rounded-lg transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
              <div className="grid grid-cols-7 border-b border-border/50">
                {DAY_LABELS.map((label) => (
                  <div key={label} className="py-2 text-center text-[10px] font-semibold text-muted-foreground/60 uppercase">{label}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 p-2 gap-1">
                {calGrid.map((day, idx) => {
                  if (day === null) return <div key={`empty-${idx}`} />;
                  const past = isDatePast(calYear, calMonth, day);
                  const selected = selectedDay === day;
                  return (
                    <button
                      key={day}
                      type="button"
                      disabled={past}
                      onClick={() => setSelectedDay(day)}
                      className={`h-8 w-full rounded-lg text-sm font-medium transition-all ${
                        selected ? 'bg-indigo-600 text-white shadow-sm'
                          : past ? 'text-muted-foreground/40 cursor-not-allowed'
                          : 'hover:bg-indigo-50 text-foreground/80'
                      }`}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="space-y-1.5 block">
              <span className="text-sm font-medium text-foreground/80">{t('dialer:bookedMeeting.hourLabel')}</span>
              <select
                value={hour}
                onChange={(e) => setHour(e.target.value)}
                className="w-full h-9 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
              >
                {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
              </select>
            </label>
            <label className="space-y-1.5 block">
              <span className="text-sm font-medium text-foreground/80">{t('dialer:bookedMeeting.minuteLabel')}</span>
              <select
                value={minute}
                onChange={(e) => setMinute(e.target.value)}
                className="w-full h-9 rounded-lg border border-border bg-card px-3 text-sm text-foreground"
              >
                {MINUTES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            {durationField}
          </div>
        </>
      )}

      <label className="space-y-1.5 block">
        <span className="text-sm font-medium text-foreground/80">{t('dialer:bookedMeeting.notesLabel')}</span>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder={t('dialer:bookedMeeting.notesPlaceholder')} />
      </label>

      <div className="flex justify-end gap-2 pt-1">
        {!compact && (
          <Button type="button" variant="ghost" onClick={() => onCancel?.()}>{t('common:actions.cancel')}</Button>
        )}
        <Button
          type="button"
          disabled={!canSubmit}
          onClick={handleConfirm}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {t('dialer:bookedMeeting.confirm')}
        </Button>
      </div>
    </div>
  );
}
